import {
  ArrowUpRight,
  Briefcase,
  Building2,
  ChevronDown,
  Clock,
  DollarSign,
  ExternalLink,
  FileText,
  Filter,
  FolderKanban,
  Globe,
  Handshake,
  Kanban,
  Mail,
  MessageSquarePlus,
  Palette,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Send,
  Star,
  Tag,
  Trash2,
  TrendingUp,
  User,
  UserPlus,
  X,
  Zap
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getProfile } from '../../config/activityProfileConfig2';
import useGrandeEcole from '../../stores/useGrandeEcole';

const COLOR_MAP = {
  zinc:    { dot:'bg-zinc-500',    text:'text-zinc-400',    border:'border-zinc-700/80',    bg:'bg-zinc-900/30',    pill:'bg-zinc-800 text-zinc-300',    accent:'orange-400' },
  sky:     { dot:'bg-sky-500',     text:'text-sky-400',     border:'border-sky-800/60',     bg:'bg-sky-950/20',     pill:'bg-sky-900/60 text-sky-300',     accent:'sky-400'     },
  amber:   { dot:'bg-amber-500',   text:'text-amber-400',   border:'border-amber-800/60',   bg:'bg-amber-950/20',   pill:'bg-amber-900/50 text-amber-300',  accent:'amber-400'  },
  fuchsia: { dot:'bg-fuchsia-500', text:'text-fuchsia-400', border:'border-fuchsia-800/60', bg:'bg-fuchsia-950/20', pill:'bg-fuchsia-900/50 text-fuchsia-300', accent:'fuchsia-400' },
  emerald: { dot:'bg-emerald-500', text:'text-emerald-400', border:'border-emerald-800/60', bg:'bg-emerald-950/20', pill:'bg-emerald-900/50 text-emerald-300', accent:'emerald-400' },
  purple:  { dot:'bg-purple-500',  text:'text-purple-400',  border:'border-purple-800/60',  bg:'bg-purple-950/20',  pill:'bg-purple-900/50 text-purple-300',  accent:'purple-400'  },
  red:     { dot:'bg-red-500',     text:'text-red-400',     border:'border-red-800/60',     bg:'bg-red-950/20',     pill:'bg-red-900/50 text-red-300',       accent:'red-400'     },
  orange:  { dot:'bg-orange-500',  text:'text-orange-400',  border:'border-orange-800/60',  bg:'bg-orange-950/20',  pill:'bg-orange-900/50 text-orange-300',  accent:'orange-400'  },
};
const c = (color, key) => COLOR_MAP[color]?.[key] ?? COLOR_MAP.zinc[key];

function useLocalStorageState(key, initial) {
  const [val, setVal] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key) ?? 'null') ?? initial; } catch { return initial; }
  });
  const set = useCallback((v) => {
    const next = typeof v === 'function' ? v(val) : v;
    setVal(next);
    try { localStorage.setItem(key, JSON.stringify(next)); } catch {}
  }, [key, val]);
  return [val, set];
}

function ScoreBar({ score, color = 'fuchsia', size = 'md' }) {
  const barColor = score >= 80 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = score >= 80 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400';
  return (
    <div className={`flex items-center gap-2 ${size === 'sm' ? 'w-20' : 'w-28'}`}>
      <div className="flex-1 h-1.5 bg-zinc-800 overflow-hidden">
        <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${Math.min(100, score)}%` }} />
      </div>
      <span className={`text-[10px] font-black font-mono ${textColor} w-6 text-right`}>{score}</span>
    </div>
  );
}

function SpondeiHandoff({ contact, onClose }) {
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const handleExport = async () => {
    setSending(true);
    try {
      // Tentative API — silencieuse si indisponible
      await fetch('/api/crm/spondei/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact,
          source: 'grande-ecole-crm',
          timestamp: new Date().toISOString(),
        }),
      }).catch(() => null); // ne bloque pas si l'API n'existe pas encore

      setDone(true);
      setTimeout(onClose, 1800);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/75 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-zinc-950 border border-fuchsia-900/50 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-black/30">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 bg-fuchsia-950/60 border border-fuchsia-800/60 flex items-center justify-center">
              <Send size={12} className="text-fuchsia-400" />
            </div>
            <div>
              <h3 className="text-xs font-black uppercase tracking-tighter text-fuchsia-300">
                Handoff Spondei
              </h3>
              <p className="text-[9px] text-zinc-600 font-mono">Transfert vers CRM partenaire</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {done ? (
            <div className="text-center py-4">
              <div className="w-10 h-10 bg-emerald-950/40 border border-emerald-800/60 rounded-full flex items-center justify-center mx-auto mb-3">
                <Handshake size={20} className="text-emerald-400" />
              </div>
              <p className="text-emerald-400 font-black text-xs uppercase tracking-widest">Transféré</p>
              <p className="text-zinc-600 text-[10px] mt-1">Contact envoyé vers Spondei</p>
            </div>
          ) : (
            <>
              {/* Récap contact */}
              <div className="bg-black/40 border border-zinc-800 p-3 space-y-1.5">
                <div className="text-xs font-bold text-zinc-200">
                  {contact.prenom ? `${contact.prenom} ${contact.nom}` : contact.nom}
                </div>
                {contact.email && (
                  <div className="text-[10px] text-zinc-500 font-mono flex items-center gap-1.5">
                    <Mail size={9} />{contact.email}
                  </div>
                )}
                {contact.entreprise && (
                  <div className="text-[10px] text-zinc-500 font-mono flex items-center gap-1.5">
                    <Building2 size={9} />{contact.entreprise}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="text-[10px] text-zinc-600 border border-zinc-800/60 bg-zinc-900/30 px-3 py-2 leading-relaxed">
                Ce contact sera transmis à <span className="text-fuchsia-400 font-mono">spondei.com</span> via l'API partenaire.
                Il apparaîtra dans votre espace Spondei avec les données de base.
              </div>

              <a
                href="https://spondei.com"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-[9px] text-fuchsia-500/60 hover:text-fuchsia-400 transition-colors"
              >
                <ExternalLink size={9} />
                Ouvrir Spondei
              </a>

              <button
                onClick={handleExport}
                disabled={sending}
                className="w-full py-3 bg-fuchsia-700 hover:bg-fuchsia-600 disabled:bg-zinc-800 disabled:text-zinc-600 text-white text-[10px] font-black uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
              >
                {sending ? (
                  <><RefreshCw size={12} className="animate-spin" /> Envoi…</>
                ) : (
                  <><Send size={12} /> Envoyer vers Spondei</>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactCard({ contact, profile, onClick, onMove, onDelete, onSpondei }) {
  const stages = profile.crmConfig.pipelineStages;
  const currentIdx = stages.findIndex(s => s.id === contact.stage);
  const isHandoffable = ['livre', 'perdu', 'closed', 'gagne', 'signe', 'diplome'].includes(contact.stage);

  return (
    <div
      onClick={() => onClick(contact)}
      className="group cursor-pointer p-3 border border-zinc-800/80 bg-zinc-900/50 hover:bg-zinc-900 hover:border-zinc-700/80 transition-all space-y-2"
    >
      {/* Nom + type */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-black text-zinc-200 truncate leading-tight uppercase tracking-tight">
            {contact.prenom ? `${contact.prenom} ${contact.nom}` : contact.nom}
          </div>
          <div className="text-[9px] text-zinc-600 font-mono mt-0.5 flex items-center gap-1 truncate">
            {contact.type_contact === 'artiste'   && <Palette size={8} className="text-fuchsia-500 flex-shrink-0" />}
            {contact.type_contact === 'client'    && <Briefcase size={8} className="text-sky-500 flex-shrink-0" />}
            {contact.type_contact === 'partenaire'&& <Building2 size={8} className="text-amber-500 flex-shrink-0" />}
            {!contact.type_contact                && <User size={8} className="flex-shrink-0" />}
            <span className="truncate">{contact.type_contact || 'contact'}{contact.specialite ? ` · ${contact.specialite}` : ''}</span>
          </div>
        </div>
        <button
          onClick={e => { e.stopPropagation(); onDelete(contact._id ?? contact.id); }}
          className="opacity-0 group-hover:opacity-100 p-0.5 text-zinc-700 hover:text-red-400 transition-all flex-shrink-0"
        >
          <X size={10} />
        </button>
      </div>

      <ScoreBar score={contact.score ?? 0} size="sm" />

      {/* Portfolio badge */}
      {contact.portfolio_url && (
        <a
          href={contact.portfolio_url}
          target="_blank"
          rel="noreferrer"
          onClick={e => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-[9px] text-fuchsia-500 hover:text-fuchsia-300 transition-colors"
        >
          <Globe size={8} /> portfolio
        </a>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {contact.email && (
            <a href={`mailto:${contact.email}`} onClick={e => e.stopPropagation()}
               className="p-1 bg-zinc-800/60 hover:bg-fuchsia-950/60 hover:text-fuchsia-400 text-zinc-600 transition-colors">
              <Mail size={9} />
            </a>
          )}
          {contact.telephone && (
            <a href={`tel:${contact.telephone}`} onClick={e => e.stopPropagation()}
               className="p-1 bg-zinc-800/60 hover:bg-emerald-950/60 hover:text-emerald-400 text-zinc-600 transition-colors">
              <Phone size={9} />
            </a>
          )}
          {isHandoffable && (
            <button
              onClick={e => { e.stopPropagation(); onSpondei(contact); }}
              className="p-1 bg-zinc-800/60 hover:bg-fuchsia-950/60 hover:text-fuchsia-400 text-zinc-600 transition-colors"
              title="Handoff → Spondei"
            >
              <Send size={9} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          {currentIdx > 0 && (
            <button onClick={e => { e.stopPropagation(); onMove(contact._id ?? contact.id, stages[currentIdx - 1].id); }}
                    className="p-0.5 text-zinc-700 hover:text-zinc-300 text-sm leading-none">‹</button>
          )}
          {currentIdx < stages.length - 1 && (
            <button onClick={e => { e.stopPropagation(); onMove(contact._id ?? contact.id, stages[currentIdx + 1].id); }}
                    className="p-0.5 text-zinc-700 hover:text-emerald-400 text-sm leading-none">›</button>
          )}
        </div>
      </div>

      {/* Tags */}
      {contact.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {contact.tags.slice(0, 3).map(tag => (
            <span key={tag} className="text-[8px] px-1 py-0.5 bg-zinc-800/80 text-zinc-500 font-mono">#{tag}</span>
          ))}
        </div>
      )}

      {/* Notes count */}
      {contact.notes?.length > 0 && (
        <div className="text-[9px] text-zinc-700 flex items-center gap-1">
          <FileText size={8} /> {contact.notes.length}
        </div>
      )}
    </div>
  );
}

function ContactDrawer({ contact, profile, onClose, onUpdate, onAddNote, onMove, onSpondei }) {
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [editScore, setEditScore] = useState(false);
  const [scoreVal, setScoreVal] = useState(contact.score ?? 50);
  const stages = profile.crmConfig.pipelineStages;
  const currentStage = stages.find(s => s.id === contact.stage) ?? stages[0];
  const isHandoffable = ['livre', 'perdu', 'closed', 'gagne', 'signe', 'diplome'].includes(contact.stage);

  const handleNoteSubmit = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    await onAddNote(contact._id ?? contact.id, newNote.trim());
    setNewNote('');
    setAddingNote(false);
  };

  return (
    <div className="fixed inset-0 z-[90] flex justify-end">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0c0f14] border-l border-zinc-800 flex flex-col h-full overflow-hidden shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-black/40 shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center text-sm ${c(currentStage.color, 'bg')} border ${c(currentStage.color, 'border')}`}>
              {currentStage.icon}
            </div>
            <div>
              <div className="text-sm font-black text-zinc-100 uppercase tracking-tighter">
                {contact.prenom ? `${contact.prenom} ${contact.nom}` : contact.nom}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[9px] font-mono uppercase ${c(currentStage.color, 'text')}`}>
                  {currentStage.label}
                </span>
                {contact.type_contact && (
                  <>
                    <span className="text-zinc-700">·</span>
                    <span className="text-[9px] text-zinc-500 font-mono">{contact.type_contact}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {isHandoffable && (
              <button
                onClick={() => onSpondei(contact)}
                className="flex items-center gap-1 px-2 py-1 bg-fuchsia-950/40 border border-fuchsia-800/40 text-fuchsia-400 hover:bg-fuchsia-950/70 text-[9px] uppercase tracking-widest transition-colors"
                title="Handoff vers Spondei"
              >
                <Send size={10} />
                <span className="hidden sm:inline">Spondei</span>
              </button>
            )}
            <button onClick={onClose} className="p-1.5 text-zinc-600 hover:text-white transition-colors">
              <X size={15} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto divide-y divide-zinc-800/40">

          {/* Score */}
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">
                {profile.crmConfig.scoreLabel}
              </span>
              <button onClick={() => setEditScore(!editScore)}
                      className="text-[9px] text-zinc-600 hover:text-zinc-400 transition-colors">
                {editScore ? 'Valider' : 'Modifier'}
              </button>
            </div>
            {editScore ? (
              <div className="flex items-center gap-3">
                <input type="range" min={0} max={100} value={scoreVal}
                       onChange={e => setScoreVal(Number(e.target.value))}
                       onMouseUp={() => onUpdate(contact._id ?? contact.id, { score: scoreVal })}
                       className="flex-1 accent-fuchsia-500" />
                <span className="text-sm font-black font-mono text-fuchsia-400 w-8">{scoreVal}</span>
              </div>
            ) : (
              <ScoreBar score={contact.score ?? 0} />
            )}
          </div>

          {/* Pipeline */}
          <div className="px-5 py-4 space-y-2">
            <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">
              {profile.vocabulary.pipeline}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {stages.map(stage => (
                <button
                  key={stage.id}
                  onClick={() => onMove(contact._id ?? contact.id, stage.id)}
                  className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wide border transition-all ${
                    contact.stage === stage.id
                      ? `${c(stage.color, 'bg')} ${c(stage.color, 'border')} ${c(stage.color, 'text')} font-black`
                      : 'bg-black/40 border-zinc-800 text-zinc-600 hover:text-zinc-300 hover:border-zinc-700'
                  }`}
                >
                  {stage.icon} {stage.label}
                </button>
              ))}
            </div>
          </div>

          {/* Coordonnées + champs créatifs */}
          <div className="px-5 py-4">
            <div className="space-y-0 border border-zinc-800/60 bg-black/20 divide-y divide-zinc-800/40">
              {[
                { icon: Mail,      key: 'email',         label: 'Email',         href: v => `mailto:${v}` },
                { icon: Phone,     key: 'telephone',     label: 'Tél.',          href: v => `tel:${v}` },
                { icon: Briefcase, key: 'specialite',    label: 'Spécialité',    href: null },
                { icon: Building2, key: 'entreprise',    label: profile.vocabulary.company, href: null },
                { icon: Globe,     key: 'portfolio_url', label: 'Portfolio',     href: v => v, external: true },
                { icon: DollarSign,key: 'budget_estime', label: 'Budget',        href: null, format: v => v ? `${Number(v).toLocaleString('fr-FR')} €` : '—' },
              ].filter(f => contact[f.key])
                .map(({ icon: Icon, key, label, href, format, external }) => (
                  <div key={key} className="flex items-center gap-3 px-3 py-2">
                    <Icon size={12} className="text-zinc-700 flex-shrink-0" />
                    <span className="text-[9px] text-zinc-600 w-16 flex-shrink-0 uppercase tracking-wider">{label}</span>
                    {href ? (
                      <a href={href(contact[key])}
                         target={external ? '_blank' : undefined}
                         rel={external ? 'noreferrer' : undefined}
                         className="text-[10px] text-fuchsia-400 hover:text-fuchsia-300 truncate flex-1 font-mono underline underline-offset-2">
                        {format ? format(contact[key]) : contact[key]}
                      </a>
                    ) : (
                      <span className="text-[10px] text-zinc-300 truncate flex-1 font-mono">
                        {format ? format(contact[key]) : contact[key]}
                      </span>
                    )}
                  </div>
                ))}
            </div>
          </div>

          {/* Tags */}
          {contact.tags?.length > 0 && (
            <div className="px-5 py-3">
              <div className="flex items-center gap-2 mb-2">
                <Tag size={11} className="text-zinc-700" />
                <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">Tags</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {contact.tags.map(tag => (
                  <span key={tag} className="text-[9px] px-1.5 py-0.5 bg-zinc-800/80 border border-zinc-700/60 text-zinc-500 font-mono">#{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText size={11} className="text-zinc-700" />
                <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">
                  {profile.crmConfig.noteLabel}
                </span>
              </div>
              <span className="text-[9px] text-zinc-700 font-mono">{contact.notes?.length ?? 0}</span>
            </div>

            {/* Saisie note */}
            <div className="flex gap-2">
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Ajouter une note…"
                rows={2}
                className="flex-1 bg-black/60 border border-zinc-800 p-2 text-[10px] text-zinc-300 placeholder-zinc-700 focus:border-zinc-600 outline-none resize-none font-mono"
              />
              <button
                onClick={handleNoteSubmit}
                disabled={!newNote.trim() || addingNote}
                className="px-2 bg-fuchsia-950/40 border border-fuchsia-800/40 text-fuchsia-400 hover:bg-fuchsia-950/60 disabled:opacity-30 transition-colors self-end py-1"
              >
                <MessageSquarePlus size={12} />
              </button>
            </div>

            {/* Liste notes */}
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {(contact.notes ?? []).map(note => (
                <div key={note.id} className="px-3 py-2 bg-zinc-900/50 border border-zinc-800/50 space-y-1">
                  <p className="text-[10px] text-zinc-300 leading-relaxed">{note.text}</p>
                  <div className="text-[9px] text-zinc-700 font-mono flex items-center gap-2">
                    <Clock size={8} />
                    {new Date(note.createdAt).toLocaleString('fr-FR')}
                    {note.author && <span>· {note.author}</span>}
                  </div>
                </div>
              ))}
              {!contact.notes?.length && (
                <p className="text-[10px] text-zinc-700 italic text-center py-2">Aucune note</p>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="px-5 py-3 text-[9px] text-zinc-700 font-mono space-y-0.5">
            <div>Créé : {new Date(contact.createdAt).toLocaleString('fr-FR')}</div>
            <div>Modifié : {new Date(contact.updatedAt).toLocaleString('fr-FR')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewContactModal({ profile, onClose, onCreate }) {
  const stages = profile.crmConfig.pipelineStages;
  const [form, setForm] = useState({
    nom: '', prenom: '', email: '', telephone: '',
    type_contact: 'artiste', specialite: '', portfolio_url: '', budget_estime: '',
    stage: stages[0]?.id ?? 'prospect', score: 60, tags: '',
  });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.nom.trim()) return;
    setSaving(true);
    await onCreate({
      ...form,
      budget_estime: Number(form.budget_estime) || 0,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    });
    setSaving(false);
    onClose();
  };

  const Input = ({ k, label, ...props }) => (
    <div>
      <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">{label}</label>
      <input value={form[k]} onChange={e => f(k, e.target.value)} {...props}
             className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-fuchsia-500 animate-pulse" />
            <h3 className="text-xs font-black uppercase tracking-tighter text-fuchsia-300">
              Nouveau {profile.vocabulary.contact}
            </h3>
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors"><X size={14} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input k="prenom" label="Prénom" />
            <Input k="nom" label="Nom *" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input k="email" label="Email" type="email" />
            <Input k="telephone" label="Téléphone" />
          </div>

          {/* Type + Spécialité */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Type</label>
              <select value={form.type_contact} onChange={e => f('type_contact', e.target.value)}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                <option value="artiste">🎨 Artiste</option>
                <option value="client">💼 Client</option>
                <option value="partenaire">🤝 Partenaire</option>
              </select>
            </div>
            <Input k="specialite" label="Spécialité" placeholder="réalisateur, photographe…" />
          </div>

          <Input k="portfolio_url" label="Portfolio URL" placeholder="https://…" />

          <div className="grid grid-cols-2 gap-3">
            <Input k="budget_estime" label="Budget estimé (€)" type="number" min={0} />
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">
                {profile.crmConfig.scoreLabel} ({form.score})
              </label>
              <input type="range" min={0} max={100} value={form.score}
                     onChange={e => f('score', Number(e.target.value))}
                     className="w-full accent-fuchsia-500 mt-2" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Étape pipeline</label>
              <select value={form.stage} onChange={e => f('stage', e.target.value)}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                {stages.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
              </select>
            </div>
            <Input k="tags" label="Tags (virgule)" placeholder="vip, urgent…" />
          </div>

          <button
            onClick={handleSubmit}
            disabled={!form.nom.trim() || saving}
            className="w-full py-3 bg-fuchsia-700 hover:bg-fuchsia-600 disabled:bg-zinc-800 disabled:text-zinc-600 text-white text-[10px] font-black uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
          >
            <UserPlus size={12} />
            {saving ? 'Création…' : `Créer ${profile.vocabulary.contact}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabProjets({ courses, learners }) {
  // Lecture des courses existantes — aucune duplication
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return courses;
    const q = search.toLowerCase();
    return courses.filter(c =>
      (c.nom || c.title || c.slug || '').toLowerCase().includes(q)
    );
  }, [courses, search]);

  return (
    <div className="space-y-4">
      {/* Notice pont */}
      <div className="flex items-start gap-2.5 p-3 border border-fuchsia-900/30 bg-fuchsia-950/10 text-[10px] text-fuchsia-400/70">
        <ArrowUpRight size={12} className="flex-shrink-0 mt-0.5" />
        <span>
          Vue lecture des projets actifs depuis la section{' '}
          <strong className="text-fuchsia-400">Parcours</strong>.
          Pour créer ou modifier, utilisez la section dédiée.
        </span>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Rechercher un projet…"
               className="w-full bg-black/60 border border-zinc-800 pl-8 pr-3 py-2 text-[10px] text-zinc-200 placeholder-zinc-700 focus:border-zinc-600 outline-none font-mono" />
      </div>

      {/* Table */}
      <div className="border border-zinc-800 bg-black/40 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-[9px] uppercase tracking-widest text-zinc-600">
          <span className="col-span-5">Nom du projet</span>
          <span className="col-span-3">Statut</span>
          <span className="col-span-2">Apprenants</span>
          <span className="col-span-2 text-right">Lien</span>
        </div>
        <div className="divide-y divide-zinc-800/30 max-h-[32rem] overflow-y-auto">
          {filtered.map((course, idx) => (
            <div key={course._id ?? idx} className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-zinc-900/30">
              <div className="col-span-5 min-w-0">
                <div className="text-[10px] font-bold text-zinc-200 truncate">
                  {course.nom || course.title || course.slug}
                </div>
                {course.description && (
                  <div className="text-[9px] text-zinc-600 truncate mt-0.5">{course.description}</div>
                )}
              </div>
              <div className="col-span-3">
                <span className={`text-[9px] px-1.5 py-0.5 font-mono uppercase ${
                  (course.status || course.visibility) === 'published' ? 'bg-emerald-900/50 text-emerald-300'
                  : (course.status || course.visibility) === 'draft'     ? 'bg-amber-900/50 text-amber-300'
                  : 'bg-zinc-800 text-zinc-500'
                }`}>
                  {course.status || course.visibility || 'N/A'}
                </span>
              </div>
              <div className="col-span-2 text-[10px] text-zinc-500 font-mono">
                {learners.filter(l => l.courseId === course._id).length || course.enrolledCount || '—'}
              </div>
              <div className="col-span-2 flex justify-end">
                <a href={`/parcours/${course._id || course.slug}`} target="_blank" rel="noreferrer"
                   className="p-1 text-zinc-700 hover:text-fuchsia-400 transition-colors">
                  <ExternalLink size={11} />
                </a>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <FolderKanban size={28} className="text-zinc-800 mx-auto mb-2" />
              <p className="text-zinc-600 text-[10px] uppercase tracking-widest">Aucun projet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TabArtistes({ learners }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search.trim()) return learners;
    const q = search.toLowerCase();
    return learners.filter(l =>
      [l.nom, l.prenom, l.email].filter(Boolean).some(f => f.toLowerCase().includes(q))
    );
  }, [learners, search]);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2.5 p-3 border border-fuchsia-900/30 bg-fuchsia-950/10 text-[10px] text-fuchsia-400/70">
        <ArrowUpRight size={12} className="flex-shrink-0 mt-0.5" />
        <span>
          Vue lecture des profils depuis la section{' '}
          <strong className="text-fuchsia-400">Apprenants</strong>.
          Pour gérer les profils, utilisez la section dédiée.
        </span>
      </div>

      <div className="relative">
        <Search size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Rechercher un talent…"
               className="w-full bg-black/60 border border-zinc-800 pl-8 pr-3 py-2 text-[10px] text-zinc-200 placeholder-zinc-700 focus:border-zinc-600 outline-none font-mono" />
      </div>

      <div className="border border-zinc-800 bg-black/40 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-[9px] uppercase tracking-widest text-zinc-600">
          <span className="col-span-4">Talent</span>
          <span className="col-span-4">Contact</span>
          <span className="col-span-2">Progression</span>
          <span className="col-span-2">Statut</span>
        </div>
        <div className="divide-y divide-zinc-800/30 max-h-[28rem] overflow-y-auto">
          {filtered.map((learner, idx) => (
            <div key={learner._id ?? learner.userId ?? idx} className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-zinc-900/30">
              <div className="col-span-4 min-w-0">
                <div className="text-[10px] font-bold text-zinc-200 truncate">
                  {learner.prenom || learner.nom ? `${learner.prenom || ''} ${learner.nom || ''}`.trim() : learner.email}
                </div>
              </div>
              <div className="col-span-4 min-w-0">
                <div className="text-[9px] text-zinc-500 font-mono truncate">{learner.email}</div>
              </div>
              <div className="col-span-2">
                <ScoreBar score={learner.globalProgress ?? 0} size="sm" />
              </div>
              <div className="col-span-2">
                <span className={`text-[9px] font-mono uppercase ${
                  learner.status === 'active' ? 'text-emerald-400' : 'text-zinc-600'
                }`}>
                  {learner.status === 'active' ? 'Actif' : learner.status || 'N/A'}
                </span>
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="py-12 text-center">
              <User size={28} className="text-zinc-800 mx-auto mb-2" />
              <p className="text-zinc-600 text-[10px] uppercase tracking-widest">Aucun talent</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TabFinances({ finances, setFinances }) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleSave = (data) => {
    if (editing) {
      setFinances(p => p.map(f => f.id === editing.id ? { ...f, ...data, updatedAt: new Date().toISOString() } : f));
    } else {
      setFinances(p => [{ id: `fin_${Date.now()}`, ...data, createdAt: new Date().toISOString() }, ...p]);
    }
    setShowModal(false);
    setEditing(null);
  };

  const totals = useMemo(() => ({
    revenus:  finances.filter(f => f.type === 'revenu').reduce((a, f) => a + (f.montant ?? 0), 0),
    depenses: finances.filter(f => f.type === 'depense').reduce((a, f) => a + (f.montant ?? 0), 0),
  }), [finances]);

  return (
    <div className="space-y-4">
      {/* KPI */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Revenus', value: totals.revenus, color: 'text-emerald-400' },
          { label: 'Dépenses', value: totals.depenses, color: 'text-red-400' },
          { label: 'Balance', value: totals.revenus - totals.depenses, color: (totals.revenus - totals.depenses) >= 0 ? 'text-emerald-400' : 'text-red-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="border border-zinc-800 bg-black/40 p-3">
            <div className="text-[9px] uppercase tracking-widest text-zinc-600">{label}</div>
            <div className={`text-xl font-black font-mono ${color}`}>
              {value.toLocaleString('fr-FR')} €
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center">
        <span className="text-[9px] uppercase tracking-widest text-zinc-600">
          {finances.length} transaction{finances.length > 1 ? 's' : ''}
        </span>
        <button onClick={() => { setEditing(null); setShowModal(true); }}
                className="flex items-center gap-1.5 px-3 py-2 bg-fuchsia-700/70 hover:bg-fuchsia-700 text-white text-[9px] uppercase tracking-widest font-bold transition-colors">
          <Plus size={11} /> Ajouter
        </button>
      </div>

      <div className="border border-zinc-800 bg-black/40">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 text-[9px] uppercase tracking-widest text-zinc-600">
          <span className="col-span-4">Description</span>
          <span className="col-span-2">Type</span>
          <span className="col-span-2">Montant</span>
          <span className="col-span-2">Date</span>
          <span className="col-span-1">Statut</span>
          <span className="col-span-1 text-right">···</span>
        </div>
        <div className="divide-y divide-zinc-800/30 max-h-[32rem] overflow-y-auto">
          {finances.map(f => (
            <div key={f.id} onClick={() => { setEditing(f); setShowModal(true); }}
                 className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-zinc-900/30 cursor-pointer">
              <div className="col-span-4 text-[10px] font-bold text-zinc-200 truncate">{f.description}</div>
              <div className="col-span-2">
                <span className={`text-[9px] px-1.5 py-0.5 font-mono ${f.type === 'revenu' ? 'bg-emerald-900/50 text-emerald-300' : 'bg-red-900/50 text-red-300'}`}>
                  {f.type === 'revenu' ? '↑' : '↓'} {f.type}
                </span>
              </div>
              <div className="col-span-2 text-[10px] font-mono font-bold text-zinc-300">
                {Number(f.montant).toLocaleString('fr-FR')} €
              </div>
              <div className="col-span-2 text-[9px] text-zinc-600 font-mono">{f.date}</div>
              <div className="col-span-1">
                <span className={`text-[9px] font-mono ${
                  f.statut === 'payé' ? 'text-emerald-400' : f.statut === 'en_attente' ? 'text-amber-400' : 'text-zinc-600'
                }`}>{f.statut}</span>
              </div>
              <div className="col-span-1 flex justify-end">
                <button onClick={e => { e.stopPropagation(); setFinances(p => p.filter(x => x.id !== f.id)); }}
                        className="p-1 text-zinc-700 hover:text-red-400 transition-colors"><Trash2 size={10} /></button>
              </div>
            </div>
          ))}
          {finances.length === 0 && (
            <div className="py-14 text-center">
              <DollarSign size={28} className="text-zinc-800 mx-auto mb-2" />
              <p className="text-zinc-600 text-[10px]">Aucune transaction</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <FinanceModal item={editing} onSave={handleSave} onClose={() => { setShowModal(false); setEditing(null); }} />
      )}
    </div>
  );
}

function FinanceModal({ item, onSave, onClose }) {
  const [form, setForm] = useState({
    description: item?.description || '', type: item?.type || 'revenu',
    montant: item?.montant || '', date: item?.date || new Date().toISOString().split('T')[0],
    statut: item?.statut || 'en_attente',
  });
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-zinc-950 border border-zinc-800 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-black/30">
          <h3 className="text-xs font-black uppercase tracking-tighter text-fuchsia-300">
            {item ? 'Modifier' : 'Ajouter'} une transaction
          </h3>
          <button onClick={onClose} className="text-zinc-600 hover:text-white"><X size={13} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Description *</label>
            <input value={form.description} onChange={e => f('description', e.target.value)}
                   className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Type</label>
              <select value={form.type} onChange={e => f('type', e.target.value)}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                <option value="revenu">↑ Revenu</option>
                <option value="depense">↓ Dépense</option>
              </select>
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Montant (€)</label>
              <input type="number" value={form.montant} onChange={e => f('montant', e.target.value)}
                     className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => f('date', e.target.value)}
                     className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Statut</label>
              <select value={form.statut} onChange={e => f('statut', e.target.value)}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                <option value="en_attente">En attente</option>
                <option value="payé">Payé</option>
                <option value="planifié">Planifié</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={() => onSave({ ...form, montant: Number(form.montant) || 0 })}
                    disabled={!form.description.trim()}
                    className="flex-1 py-2.5 bg-fuchsia-700 hover:bg-fuchsia-600 disabled:bg-zinc-800 text-white text-[10px] font-black uppercase tracking-widest">
              {item ? 'Enregistrer' : 'Créer'}
            </button>
            <button onClick={onClose} className="flex-1 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 text-[10px] uppercase tracking-widest">
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabPartenaires({ onSpondeiClick }) {
  const partners = [
    {
      id: 'spondei',
      nom: 'Spondei',
      description: 'CRM partenaire — transfert de contacts communs',
      lien: 'https://spondei.com',
      color: 'fuchsia',
      badge: 'Connecté',
      highlight: true,
    },

  ];

  return (
    <div className="space-y-4">
      <div className="text-[9px] uppercase tracking-widest text-zinc-600">
        {partners.length} partenaires
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {partners.map(p => (
          <div
            key={p.id}
            className={`border bg-black/40 p-4 group transition-colors ${
              p.highlight
                ? 'border-fuchsia-800/50 bg-fuchsia-950/10 hover:bg-fuchsia-950/20'
                : 'border-zinc-800 hover:bg-zinc-900/30 hover:border-zinc-700'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className={`text-xs font-black uppercase tracking-tight ${c(p.color, 'text')}`}>
                    {p.nom}
                  </h3>
                  {p.badge && (
                    <span className={`text-[8px] px-1.5 py-0.5 font-mono uppercase ${c(p.color, 'pill')}`}>
                      {p.badge}
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed">{p.description}</p>
              </div>
              <a href={p.lien} target="_blank" rel="noreferrer"
                 className={`p-2 border transition-colors flex-shrink-0 ${c(p.color, 'bg')} ${c(p.color, 'border')} ${c(p.color, 'text')} hover:opacity-80`}>
                <ExternalLink size={13} />
              </a>
            </div>

            {/* Action Spondei */}
            {p.id === 'spondei' && (
              <button
                onClick={onSpondeiClick}
                className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 border border-fuchsia-800/50 bg-fuchsia-950/20 text-fuchsia-400 hover:bg-fuchsia-950/40 text-[9px] uppercase tracking-widest font-bold transition-colors"
              >
                <Send size={10} />
                Handoff un contact vers Spondei
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const TABS = [
  { key: 'pipeline',    label: 'Pipeline',    icon: Kanban      },
  { key: 'projets',     label: 'Projets',     icon: FolderKanban },
  { key: 'profils',     label: 'Profils',     icon: User        },
  { key: 'finances',    label: 'Finances',    icon: DollarSign  },
  { key: 'partenaires', label: 'Partenaires', icon: Handshake   },
];

export default function CRMCreativePanel({ darkMode = true }) {
  const {
    activityProfile,
    crmContacts, crmLoading, crmSearch, crmStageFilter,
    selectedContact, learners, courses,
    fetchCrmContacts, createCrmContact, updateCrmContact,
    deleteCrmContact, moveCrmContactStage, addCrmNote,
    setCrmSearch, setCrmStageFilter, setSelectedContact,
    clearSelectedContact, filteredCrmContacts,
  } = useGrandeEcole();

  const profile = getProfile(activityProfile);
  const stages  = profile.crmConfig.pipelineStages;

  const [activeTab, setActiveTab]   = useState('pipeline');
  const [showNew, setShowNew]       = useState(false);
  const [expandedCols, setExpanded] = useState({});
  const [spondeiContact, setSpondeiContact] = useState(null); // contact à handoff

  // Données locales légères
  const [finances, setFinances] = useLocalStorageState('crm_creative_finances', []);

  const filtered = useMemo(
    () => filteredCrmContacts?.() ?? crmContacts,
    [crmContacts, crmSearch, crmStageFilter, filteredCrmContacts]
  );

  const byStage = useMemo(() => {
    const map = {};
    stages.forEach(s => { map[s.id] = []; });
    filtered.forEach(ct => {
      if (map[ct.stage]) map[ct.stage].push(ct);
      else if (stages[0]) map[stages[0].id].push(ct);
    });
    return map;
  }, [filtered, stages]);

  const kpis = useMemo(() => {
    const total    = crmContacts.length;
    const actifs   = crmContacts.filter(ct => !['perdu', 'churned', 'inactif', 'abandon'].includes(ct.stage)).length;
    const convertiId = stages.at(-2)?.id;
    const convertis  = convertiId ? crmContacts.filter(ct => ct.stage === convertiId).length : 0;
    const avgScore  = total > 0
      ? Math.round(crmContacts.reduce((a, ct) => a + (ct.score ?? 0), 0) / total)
      : 0;
    return { total, actifs, convertis, avgScore };
  }, [crmContacts, stages]);

  useEffect(() => { fetchCrmContacts(); }, []);

  const toggleCol = (id) => setExpanded(p => ({ ...p, [id]: p[id] === false ? true : false }));

  // ── RENDU ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5 font-mono">

      {/* ── ONGLETS ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap border-b border-zinc-800">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-[10px] uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap ${
              activeTab === key
                ? 'border-fuchsia-500 text-fuchsia-300 bg-fuchsia-950/15'
                : 'border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900/20'
            }`}
          >
            <Icon size={12} />
            {label}
          </button>
        ))}
      </div>

      {/* ── PIPELINE ────────────────────────────────────────────────────── */}
      {activeTab === 'pipeline' && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: profile.vocabulary.contacts, value: kpis.total,    color: 'text-zinc-300',    icon: User,       sub: `${kpis.actifs} actifs` },
              { label: stages.at(-2)?.label ?? 'Livrés', value: kpis.convertis, color: 'text-emerald-400', icon: Zap, sub: kpis.total > 0 ? `${Math.round(kpis.convertis / kpis.total * 100)}%` : '—' },
              { label: profile.crmConfig.scoreLabel, value: kpis.avgScore, color: kpis.avgScore >= 70 ? 'text-emerald-400' : kpis.avgScore >= 40 ? 'text-amber-400' : 'text-red-400', icon: Star, sub: 'Moyen' },
              { label: 'Étapes', value: stages.length, color: 'text-fuchsia-400', icon: TrendingUp, sub: profile.vocabulary.pipeline },
            ].map(({ label, value, color, icon: Icon, sub }) => (
              <div key={label} className="border border-zinc-800 bg-black/40 p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] uppercase tracking-widest text-zinc-600">{label}</span>
                  <Icon size={12} className={`${color} opacity-50`} />
                </div>
                <div className={`text-2xl font-black font-mono ${color}`}>{value}</div>
                <div className="text-[9px] text-zinc-600 mt-0.5 truncate">{sub}</div>
              </div>
            ))}
          </div>

          {/* Barre de recherche + actions */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-36">
              <Search size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input value={crmSearch} onChange={e => setCrmSearch(e.target.value)}
                     placeholder={`Rechercher un ${profile.vocabulary.contact.toLowerCase()}…`}
                     className="w-full bg-black/60 border border-zinc-800 pl-8 pr-3 py-2 text-[10px] text-zinc-200 placeholder-zinc-700 focus:border-zinc-600 outline-none" />
            </div>
            <div className="relative">
              <Filter size={10} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
              <select value={crmStageFilter} onChange={e => setCrmStageFilter(e.target.value)}
                      className="bg-black/60 border border-zinc-800 pl-7 pr-7 py-2 text-[10px] text-zinc-300 focus:border-zinc-600 outline-none appearance-none">
                <option value="all">Toutes étapes</option>
                {stages.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
              </select>
              <ChevronDown size={9} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
            </div>
            <button onClick={() => setShowNew(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-fuchsia-700/80 hover:bg-fuchsia-700 text-white text-[9px] uppercase tracking-widest font-bold transition-colors">
              <Plus size={11} />
              <span className="hidden sm:inline">Nouveau</span>
            </button>
          </div>

          <div className="text-[9px] text-zinc-700 uppercase tracking-widest -mt-1">
            {filtered.length} {profile.vocabulary.contact.toLowerCase()}{filtered.length > 1 ? 's' : ''}
          </div>

          {/* Kanban */}
          <div className="overflow-x-auto pb-3">
            <div className="flex gap-2.5 min-w-max">
              {stages.map(stage => {
                const cols = byStage[stage.id] ?? [];
                const isOpen = expandedCols[stage.id] !== false;
                return (
                  <div key={stage.id}
                       className={`w-52 flex flex-col border ${c(stage.color, 'border')} ${c(stage.color, 'bg')} flex-shrink-0`}>
                    {/* En-tête colonne */}
                    <button
                      onClick={() => toggleCol(stage.id)}
                      className={`flex items-center justify-between px-3 py-2.5 border-b ${c(stage.color, 'border')} hover:bg-black/20 transition-colors`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span>{stage.icon}</span>
                        <span className={`text-[9px] font-black uppercase tracking-wider ${c(stage.color, 'text')}`}>
                          {stage.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-[9px] font-black ${c(stage.color, 'text')}`}>{cols.length}</span>
                        <ChevronDown size={10} className={`${c(stage.color, 'text')} transition-transform ${!isOpen ? '-rotate-90' : ''}`} />
                      </div>
                    </button>

                    {/* Cards */}
                    {isOpen && (
                      <div className="flex flex-col gap-1.5 p-1.5 min-h-12 max-h-[30rem] overflow-y-auto">
                        {cols.map(ct => (
                          <ContactCard
                            key={ct._id ?? ct.id}
                            contact={ct}
                            profile={profile}
                            onClick={setSelectedContact}
                            onMove={moveCrmContactStage}
                            onDelete={deleteCrmContact}
                            onSpondei={setSpondeiContact}
                          />
                        ))}
                        {cols.length === 0 && (
                          <div className="text-center py-5 text-zinc-800 text-[9px]">—</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ── PROJETS (pont) ──────────────────────────────────────────────── */}
      {activeTab === 'projets' && (
        <TabProjets courses={courses} learners={learners} />
      )}

      {/* ── Artistes (pont) ──────────────────────────────────────────────── */}
      {activeTab === 'profils' && (
        <TabArtistes learners={learners} />
      )}

      {/* ── FINANCES ────────────────────────────────────────────────────── */}
      {activeTab === 'finances' && (
        <TabFinances finances={finances} setFinances={setFinances} />
      )}

      {/* ── PARTENAIRES ─────────────────────────────────────────────────── */}
      {activeTab === 'partenaires' && (
        <TabPartenaires onSpondeiClick={() => {
          // Ouvre le handoff sur le premier contact converti/livré s'il existe
          const candidate = crmContacts.find(ct =>
            ['livre', 'closed', 'gagne', 'signe', 'diplome'].includes(ct.stage)
          );
          if (candidate) setSpondeiContact(candidate);
        }} />
      )}

      {/* ── DRAWER CONTACT ──────────────────────────────────────────────── */}
      {selectedContact && (
        <ContactDrawer
          contact={selectedContact}
          profile={profile}
          onClose={clearSelectedContact}
          onUpdate={updateCrmContact}
          onAddNote={addCrmNote}
          onMove={moveCrmContactStage}
          onSpondei={setSpondeiContact}
        />
      )}

      {/* ── MODAL NOUVEAU CONTACT ───────────────────────────────────────── */}
      {showNew && (
        <NewContactModal
          profile={profile}
          onClose={() => setShowNew(false)}
          onCreate={createCrmContact}
        />
      )}

      {/* ── SPONDEI HANDOFF ─────────────────────────────────────────────── */}
      {spondeiContact && (
        <SpondeiHandoff
          contact={spondeiContact}
          onClose={() => setSpondeiContact(null)}
        />
      )}
    </div>
  );
}